import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Collapse, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, FilterList } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getStatusColor, hexToRgba } from '../../dashboard/calendar-view/calendarColorUtils';
import { getStatusLabel } from '../../../shared/utils/status';
import './WorklogStrip.scss';

const WorklogStrip = ({ worklogData = [], filterMode = 'week', onFilterChange, loading = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const handleFilterClick = (event) => {
    event.stopPropagation();
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (event, mode) => {
    if (event) {
      event.stopPropagation();
    }
    if (mode) {
      onFilterChange?.(mode);
    }
    setFilterAnchorEl(null);
  };

  const selectedDayData = useMemo(() => {
    if (!Array.isArray(worklogData) || worklogData.length === 0) return null;
    if (filterMode === 'week') return null;

    const targetDate = new Date();
    if (filterMode === 'yesterday') {
      targetDate.setDate(targetDate.getDate() - 1);
    }

    return worklogData.find((day) => {
      if (!day?.date) return false;
      return new Date(day.date).toDateString() === targetDate.toDateString();
    }) || worklogData[filterMode === 'yesterday' ? 0 : worklogData.length - 1] || null;
  }, [filterMode, worklogData]);

  const renderWeekView = () => {
    if (loading) {
      return (
        <Box className="worklog-loading-state">
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!worklogData || worklogData.length === 0) {
      return (
        <Box className="empty-card-state">
          <InfoOutlinedIcon className="empty-icon" />
          <Typography className="empty-text">{t('dailyStandup.logStandup.noWorklog')}</Typography>
        </Box>
      );
    }

    return (
      <Box className="worklog-week-strip">
        {worklogData.map((dayData, idx) => (
          <Box key={dayData.date || dayData.day || idx} className="worklog-card">
            <Typography className="worklog-day">
              {dayData.day}, {new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Typography>
            <Typography className={`worklog-hours ${dayData.logged === '0h 0m' ? 'empty-hours' : ''}`}>
              {dayData.logged}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderDayView = () => {
    if (loading) {
      return (
        <Box className="worklog-loading-state">
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!selectedDayData) {
      return (
        <Box className="empty-card-state">
          <InfoOutlinedIcon className="empty-icon" />
          <Typography className="empty-text">{t('dailyStandup.logStandup.noWorklog')}</Typography>
        </Box>
      );
    }

    const displayDateStr = `${selectedDayData.day || ''}, ${new Date(selectedDayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    const tickets = Array.isArray(selectedDayData.tickets) ? selectedDayData.tickets : [];

    return (
      <Box className="worklog-day-list">
        {tickets.length === 0 ? (
          <Box className="empty-card-state">
            <InfoOutlinedIcon className="empty-icon" />
            <Typography className="empty-text">
              {t('dailyStandup.logStandup.noTickets', 'No tickets logged for this day')}
            </Typography>
          </Box>
        ) : tickets.map((ticket, idx) => {
          const statusLabel = getStatusLabel(ticket.status);
          const statusColor = ticket.status?.colour || ticket.status?.color || getStatusColor(statusLabel);
          return (
            <Box key={ticket.ticket_id || ticket.ticket_key || idx} className="worklog-ticket-wrapper">
              <Box
                className="ticket-row-section"
                style={{
                  '--status-color': statusColor,
                  '--status-bg': hexToRgba(statusColor, 0.1)
                }}
                onClick={() => window.open(`/task/${ticket.ticket_key}`, '_blank', 'noopener,noreferrer')}
              >
                <Typography className="ticket-key">{ticket.ticket_key}</Typography>
                <Typography className="ticket-title">{ticket.title}</Typography>
                <Typography className="ticket-status">{statusLabel}</Typography>
              </Box>
              <Box className="worklog-card side-worklog-card">
                <Typography className="worklog-day">{displayDateStr}</Typography>
                <Typography className={`worklog-hours ${ticket.logged === '0h 0m' ? 'empty-hours' : ''}`}>
                  {ticket.logged}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box className={`worklog-section ${loading ? 'updating-worklog' : ''}`}>
      <Box className="section-header" onClick={() => setExpanded(!expanded)}>
        <Typography variant="h6" className="section-title">
          {t('dailyStandup.logStandup.worklog')}
        </Typography>
        <Box className="header-actions">
          <IconButton size="small" onClick={handleFilterClick} className="filter-icon">
            <FilterList fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={(e) => handleFilterClose(e, null)}
          >
            <MenuItem selected={filterMode === 'today'} onClick={(e) => handleFilterClose(e, 'today')}>{t('common.today')}</MenuItem>
            <MenuItem selected={filterMode === 'yesterday'} onClick={(e) => handleFilterClose(e, 'yesterday')}>{t('common.yesterday')}</MenuItem>
            <MenuItem selected={filterMode === 'week'} onClick={(e) => handleFilterClose(e, 'week')}>{t('common.oneWeek')}</MenuItem>
          </Menu>
          <IconButton size="small" className="expand-icon">
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box className="worklog-content">
          {filterMode === 'week' ? renderWeekView() : renderDayView()}
        </Box>
      </Collapse>
    </Box>
  );
};

export default WorklogStrip;
